import axios from 'axios';
import { CONFIG } from '../config';

/**
 * Pre-configured Axios instance that:
 * 1. Pre-pends the backend base URL
 * 2. Injects the Bearer token from sessionStorage
 * 3. Attempts silent token refresh on 401, then redirects to login only if refresh fails
 */
const api = axios.create({
  baseURL: CONFIG.BACKEND_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true, // Send httpOnly cookies (refresh token) with every request
});

// ── Request interceptor: inject auth token ──
api.interceptors.request.use((config) => {
  const token = sessionStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// ── Response interceptor: silent refresh on 401, then redirect if refresh fails ──
let refreshPromise: Promise<string | null> | null = null;

/** Server reply when the login has no Junior account (only admins create accounts). */
export const NO_ACCOUNT = 'No Junior account';
export const NO_ACCOUNT_MESSAGE =
  'You signed in, but you do not have a Junior account. Please ask a Junior administrator to create one for you.';
/** Several requests fail at once; show the message and redirect only once. */
let noAccountNoticeShown = false;

async function silentRefresh(): Promise<string | null> {
  try {
    const res = await axios.post(
      `${CONFIG.BACKEND_BASE_URL}/api/auth/refresh`,
      {},
      { withCredentials: true },
    );
    const newToken = res.data?.tokens?.id_token ?? null;
    if (newToken) {
      sessionStorage.setItem('token', newToken);
    }
    return newToken;
  } catch {
    return null;
  }
}

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const url = originalRequest?.url ?? '';
    const isAuthEndpoint = url.includes('/api/auth/');
    const hasToken = !!sessionStorage.getItem('token');

    // Signed in (for example with Microsoft) but no Junior account exists. A refresh cannot
    // help, so say why and sign out of Cognito so its session does not sign straight back in.
    if (error.response?.status === 401 && error.response?.data?.error === NO_ACCOUNT) {
      sessionStorage.removeItem('token');
      if (!noAccountNoticeShown) {
        noAccountNoticeShown = true;
        window.alert(NO_ACCOUNT_MESSAGE);
        window.location.href = CONFIG.COGNITO_LOGOUT_URL;
      }
      return Promise.reject(error);
    }

    // Only attempt refresh on 401 if: a token existed, it's not an auth endpoint,
    // and we haven't already retried this request
    if (!isAuthEndpoint && error.response?.status === 401 && hasToken && !originalRequest._retry) {
      originalRequest._retry = true;

      // Coalesce concurrent refresh attempts into a single request
      if (!refreshPromise) {
        refreshPromise = silentRefresh().finally(() => {
          refreshPromise = null;
        });
      }

      const newToken = await refreshPromise;
      if (newToken) {
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return api(originalRequest);
      }

      // Refresh failed — clear token and redirect to login.
      // If the backend ended this session on purpose (End sessions / Deactivate), go through
      // Cognito's logout first: its own session cookie would otherwise sign the browser
      // straight back in without a password.
      sessionStorage.removeItem('token');
      const reason = error.response?.data?.error;
      const endedByAdmin = reason === 'Session revoked' || reason === 'Account deactivated';
      window.location.href = endedByAdmin ? CONFIG.COGNITO_LOGOUT_URL : CONFIG.COGNITO_LOGIN_URL;
    }

    return Promise.reject(error);
  },
);

export default api;
